import { BaseRecord } from '@refinedev/core'
import { Button, Col, Form, Input, Row, Space, Table, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useGetListCompanies } from '../../../../api/company'
import { AutoComplete } from '../../../../components/inputs/autocomplete'
import { useExportExcel } from '../../../../utils/hooks/useExportExcel.hook'
import { useIsAdminRole } from '../../../../utils/hooks/useIdentityAdmin.hook'
import { ButtonListHeader } from '../../../components/buttonsList'
import { Row as RowTable } from '../../../components/interface'
import { AddBeneficiaryModal } from './modal-add-beneficiary'
import { AddRemitterModal } from './modal-add-remitter'
import { CopyButton } from '../../../components/copyButton'
import { DownOutlined } from '@ant-design/icons'

interface IProps {
  customerId?: number
  isActive?: boolean
  menu?: React.ReactNode
}

const start = 0
const end = 20

export const CustomerBeneficiariesRemitters: React.FC<IProps> = ({
  customerId,
  isActive = true,
  menu,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const current = queryParams.get('current') ?? 1
  const pageSize = queryParams.get('pageSize') ?? 10
  const isAdminRole = useIsAdminRole()
  const { exportExcel } = useExportExcel()

  const [currentPage, setCurrentPage] = useState(current ? Number(current) : 1)
  const [params, setParams] = useState({
    start,
    end,
  })
  const [searchName, setSearchName] = useState<string>('')
  const [searchCountry, setSearchCountry] = useState<string>('')
  const [form] = Form.useForm()
  const [modalRemitterOpen, setModalRemitterOpen] = useState(false)
  const [modalBeneficiaryOpen, setModalBeneficiaryOpen] = useState(false)

  useEffect(() => {
    if (current && pageSize) {
      setParams({
        start: (Number(current) - 1) * Number(pageSize),
        end: Number(current) * Number(pageSize),
      })
    }
  }, [current, pageSize])

  const {
    data = {
      data: [],
      totalCount: 0,
      totalPages: 0,
      pageSize: 20,
      page: 0,
    },
    isLoading,
    refetch: refetchList,
  } = useGetListCompanies({
    start: params.start,
    end: params.end,
    like: searchName,
    country: searchCountry,
    userId: customerId,
    enabled: isActive,
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setParams({
      start: (page - 1) * Number(pageSize) || start,
      end: page * Number(pageSize) || end,
    })
    navigate(`?current=${page}&pageSize=${pageSize ?? 10}`, { replace: true })
  }

  const handleSearch = useCallback(
    (values: { searchValue?: string; country?: string }) => {
      const searchValue = values.searchValue?.trim() || ''
      const countryValue = values.country || ''

      setSearchName(searchValue)
      setSearchCountry(countryValue)
      setCurrentPage(1)
      setParams({ start, end })
      navigate(`?current=1&pageSize=10`, { replace: true })
    },
    [navigate]
  )

  const handleModalSuccess = useCallback(() => {
    setSearchName('')
    setSearchCountry('')
    setCurrentPage(1)
    setParams({ start, end })
    // Reset form values
    form.resetFields()
    navigate(`?current=1&pageSize=${pageSize ?? 10}`, { replace: true })
    refetchList()
  }, [navigate, pageSize, refetchList, form])

  const handClick = (record: BaseRecord) => {
    if (record.id && isAdminRole) {
      if (customerId) {
        navigate(`/customers/${record.id}/beneficiaries`)
      } else {
        navigate(`/beneficiaries-remitters/${record.id}`)
      }
    }
  }

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => {
        handClick(record)
      },
    }
  }

  const columns: ColumnType<RowTable>[] = [
    {
      dataIndex: 'customId',
      title: 'ID',
      render: (value, record: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {(record?.refUser?.smartUID || value || record?.id) && (
              <CopyButton
                value={record?.refUser?.smartUID || value || record?.id}
              />
            )}
            <Typography.Text
              onClick={() => {
                handClick(record)
              }}>
              {record?.refUser?.smartUID || value || record?.id}
            </Typography.Text>
          </div>
        )
      },
    },
    {
      dataIndex: 'fullName',
      title: 'NAME',
      onCell: handleClickOnCell,
      render: (value, record: any) => {
        return (
          <Typography.Text>
            {value ?? record?.refUser?.fullName}
          </Typography.Text>
        )
      },
    },
    {
      dataIndex: 'tinVatUid',
      title: 'VAT/TIN/UID',
      render: (value, record: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {value && <CopyButton value={value} />}
            <Typography.Text
              onClick={() => {
                handClick(record)
              }}>
              {value || '-'}
            </Typography.Text>
          </div>
        )
      },
    },
    {
      dataIndex: 'regNo',
      title: 'RED',
      onCell: handleClickOnCell,
      render: (value) => {
        return <Typography.Text>{value || '-'}</Typography.Text>
      },
    },
    {
      dataIndex: 'country',
      title: 'COUNTRY',
      onCell: handleClickOnCell,
      render: (value) => {
        return <Typography.Text>{value || '-'}</Typography.Text>
      },
    },
  ]

  return (
    <>
      <div>
        {menu ? (
          <></>
        ) : isAdminRole ? (
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
            {ButtonListHeader(() => setModalRemitterOpen(true))}
          </div>
        ) : null}

        <Form
          form={form}
          layout='vertical'
          onFinish={handleSearch}
          style={{
            background: '#FAFAFA',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            padding: '16px 10px 0',
          }}>
          <Row justify='space-between'>
            <Col lg={8} md={10}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name='searchValue' style={{ width: '100%' }}>
                  <Input
                    placeholder='Search by name'
                    allowClear
                    onPressEnter={() => form.submit()}
                    onChange={(e) => {
                      // Auto submit when cleared
                      if (!e.currentTarget.value && searchName) {
                        form.submit()
                      }
                    }}
                  />
                </Form.Item>
                <Button
                  type='primary'
                  style={{ borderRadius: '0px 6px 6px 0px', marginTop: '5px' }}
                  onClick={() => form.submit()}>
                  Search
                </Button>
              </Space.Compact>
            </Col>
            <Col
              lg={8}
              md={10}
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'end',
              }}
            >
              <Form.Item name='country' style={{ marginTop: '-4px' }}>
                <AutoComplete
                  resource='api/v1/worldCheck/countries'
                  mapItem={(item: { value: string; label: string }) => ({
                    value: item.label,
                    key: item.value,
                    label: <Typography.Text>{item.label}</Typography.Text>,
                    data: item,
                  })}
                  notFoundPlaceholder='Start by typing country name'
                  onSelect={() => {
                    // AutoComplete already sets the value, just submit
                    form.submit()
                  }}
                  onClear={() => {
                    // AutoComplete already clears the value, just submit
                    form.submit()
                  }}>
                  <Input
                    placeholder='Search country'
                    allowClear
                    onClear={() => {
                      form.submit()
                    }}
                    suffix={<DownOutlined />}
                  />
                </AutoComplete>
              </Form.Item>

              <div style={{ width: '74px', marginTop: '5px' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    exportExcel('companies/export-excel?_isProvider=false', {
                      ...(searchName && { _like: searchName }),
                      ...(searchCountry && { _country: searchCountry }),
                      ...(customerId && { _userId: customerId }),
                    })
                  }}
                >
                  Export
                </Button>
              </div>
            </Col>
          </Row>
        </Form>

        {menu}

        <Table
          rowKey='id'
          columns={columns as any}
          dataSource={data?.data || []}
          loading={isLoading}
          pagination={{
            current: currentPage,
            total: data?.totalCount || 0,
            pageSize: Number(pageSize),
            onChange: handlePageChange,
          }}
        />
      </div >
      <AddRemitterModal
        open={modalRemitterOpen}
        onClose={() => setModalRemitterOpen(false)}
        onSuccess={handleModalSuccess}
        customerId={customerId}
        refetchList={refetchList}
      />
      <AddBeneficiaryModal
        open={modalBeneficiaryOpen}
        onClose={() => setModalBeneficiaryOpen(false)}
        onSuccess={handleModalSuccess}
        customerId={customerId}
      />
    </>
  )
}
